import { generateKeyBetween } from 'fractional-indexing';

/**
 * Interface for a ticket with a position
 * Note: we just need ticketId and position for ordering tickets
 */
export interface TicketWithPosition {
  ticketId: string;
  position: string | null;
  [key: string]: unknown;
}

/**
 * Sorts tickets by their fractional index position
 * @param tickets - Array of tickets to sort
 * @returns Sorted array of tickets
 */
export const sortTicketsByPosition = <T extends TicketWithPosition>(tickets: T[]): T[] => {
  // check empty or null/undefined input
  if (!tickets || tickets?.length === 0) {
    return [];
  }

  // check for single item (no sorting needed)
  if (tickets?.length === 1) {
    return [...tickets];
  }

  return [...tickets].sort((a, b) => {
    // check for null/undefined positions - put them at the end
    if (!a.position && !b.position) return 0;
    if (!a.position) return 1;
    if (!b.position) return -1;

    return a.position < b.position ? -1 : a.position > b.position ? 1 : 0;
  });
};

/**
 * Remove an item from a list by index (immutable)
 */
export const removeAtIndex = <T>(arr: T[], index: number): T[] => arr.filter((_, i) => i !== index);

/**
 * Determine if two lists are the same reference (same column)
 */
export const isSameList = <T>(listA: T[], listB: T[]) => listA === listB;

/**
 * Generate new position key based on prev/next positions
 */
export const getNewPositionBetween = (prev: TicketWithPosition | null, next: TicketWithPosition | null): string => {
  const prevPos = prev?.position ?? null;
  const nextPos = next?.position ?? null;
  return generateKeyBetween(prevPos, nextPos);
};

/**
 * Get new position at top of list
 */
/**
 * When inserting at the top (before all others)
 */
export const getPositionAtTop = (tickets: TicketWithPosition[] | undefined): string => {
  const first = sortTicketsByPosition(tickets ?? [])[0];
  return getNewPositionBetween(null, first ?? null);
};

/**
 * When inserting at the bottom (after all others)
 */
export const getPositionAtBottom = (tickets: TicketWithPosition[] | undefined): string => {
  const last = sortTicketsByPosition(tickets ?? []).at(-1);
  return getNewPositionBetween(last ?? null, null);
};

/**
 * Generate a new fractional position when reordering a ticket.
 *
 * Handles:
 * - Moving up or down within the same list
 * - Moving to another list (e.g. another column)
 * - Empty target lists
//  */

export const reorderTickets = (
  sourceTickets: TicketWithPosition[],
  targetTickets: TicketWithPosition[],
  sourceIndex: number,
  targetIndex: number
): string => {
  // check if the move is within the same list (column)
  const sameColumn = isSameList(sourceTickets, targetTickets);

  // get the column to order
  const columnToOrder = sameColumn ? sourceTickets : targetTickets;

  // sort the column by position to maintain the order
  // const ordered = sortTicketsByPosition(columnToOrder);
  const ordered = columnToOrder;

  if (sameColumn) {
    // remove the ticket from the list
    const [moved] = ordered.splice(sourceIndex, 1);

    // insert the ticket at the target index
    ordered.splice(targetIndex, 0, moved);
  }

  // get the previous and next tickets to calculate the new position
  const prev = ordered[targetIndex - 1] ?? null;
  const next = ordered[targetIndex + (sameColumn ? 1 : 0)] ?? null;

  return getNewPositionBetween(prev, next);
};

// A....B...C
